import { z } from "zod";

// TES-998 (Ö-238) — the goal zone: what happens when a patient in active
// treatment logs a weight at or below her registered goal.
//
// Emelie 2026-09-29 (TES-996), verbatim:
// "CP-60 gäller patienter i aktiv behandling."
// "För befintliga patienter skapas goal_zone_candidate_detected när:"
// "patienten har en registrerad målvikt,"
// "en ny giltig och daterad vikt registreras från Dina värden, vecko- eller månadsincheckningen,"
// "vikten är lika med eller lägre än målvikt,"
// "patienten befinner sig i ACTIVE_TREATMENT,"
// "ingen motsvarande aktiv målbekräftelse redan finns."
// "Detta ändrar inte automatiskt klinisk fas."
//
// The confirmation is the row that makes "no matching active confirmation"
// checkable: detection creates it, her choice moves it, and the doctor's
// published weight-stabilisation decision closes it. Nothing here writes the
// clinical phase — that stays Kirill's explicit choice (see clinical-phase.ts).

// Her three choices. Stored as these values; the Swedish wording is the
// app's copy, taken verbatim from the canonical CP-60 document:
//   keep_current_weight  "Jag vill behålla ungefär den vikt jag har nu."
//   set_new_goal         "Jag vill sätta ett nytt viktmål."
//   think_about_it       "Jag vill fundera lite."
export const GOAL_ZONE_CHOICES = [
  "keep_current_weight",
  "set_new_goal",
  "think_about_it",
] as const;
export const goalZoneChoiceSchema = z.enum(GOAL_ZONE_CHOICES);
export type GoalZoneChoice = z.infer<typeof goalZoneChoiceSchema>;

// Where a confirmation stands.
//
//   awaiting_choice  detected; the choice task is open
//   thinking         she chose "fundera" — still open, she can choose later
//   keep_requested   she chose "behålla" (Drive: maintenance_requested); a
//                    wish carried into the next monthly review, CP-60 runs
//   new_goal         she set a new goal instead — closed
//   withdrawn        the triggering weight was corrected above the goal
//                    before she chose — closed
//   closed           the doctor published a weight-stabilisation decision
//   active_treatment_continued
//                    TES-1011 — the doctor answered a keep-wish at the
//                    monthly review with continued active treatment
//                    (CONTINUE_ACTIVE_TREATMENT); the phase did not change
//                    and the published review feedback is her explanation
//
// The first three are ACTIVE: while one exists no new candidate is created.
export const GOAL_ZONE_STATUSES = [
  "awaiting_choice",
  "thinking",
  "keep_requested",
  "new_goal",
  "withdrawn",
  "closed",
  "active_treatment_continued",
] as const;
export const goalZoneStatusSchema = z.enum(GOAL_ZONE_STATUSES);
export type GoalZoneStatus = z.infer<typeof goalZoneStatusSchema>;

export const ACTIVE_GOAL_ZONE_STATUSES: readonly GoalZoneStatus[] = [
  "awaiting_choice",
  "thinking",
  "keep_requested",
];

export function isActiveGoalZoneStatus(status: string): boolean {
  return (ACTIVE_GOAL_ZONE_STATUSES as readonly string[]).includes(status);
}

/**
 * TES-1011 — is a keep-wish waiting for the doctor's phase decision?
 *
 * Only `keep_requested`: that is the wish CP-60 carries into the monthly
 * review ("CP-60 får inte ligga obehandlad efter den relevanta"
 * "månadsbedömningen"). An unanswered or deferred choice is not a wish yet,
 * so it asks nothing of the doctor. Shared by the API, which refuses to
 * publish a monthly review past an open wish without a phase decision, and
 * the admin, which makes the choice required in the same case.
 */
export function isKeepWishOpen(
  confirmation: { status: string } | null | undefined,
): boolean {
  return confirmation?.status === "keep_requested";
}

export const goalZoneConfirmationSchema = z.object({
  id: z.string().uuid(),
  status: goalZoneStatusSchema,
  // The goal the weight was compared against when it was detected.
  goalWeightKg: z.number(),
  // The weight that met it, and the day it was measured.
  triggerKg: z.number(),
  triggerMeasuredOn: z.string(),
  detectedAt: z.string(),
  choice: goalZoneChoiceSchema.nullable(),
  choiceAt: z.string().nullable(),
});
export type GoalZoneConfirmation = z.infer<typeof goalZoneConfirmationSchema>;

// GET /patient-api/v1/me/goal-zone — the active confirmation, or null.
export const goalZoneStateSchema = z.object({
  confirmation: goalZoneConfirmationSchema.nullable(),
});
export type GoalZoneState = z.infer<typeof goalZoneStateSchema>;

// POST /patient-api/v1/me/goal-zone/choice. A new goal travels in the same
// call as the choice, so "Jag vill sätta ett nytt viktmål" can never be
// recorded without the goal it names. Same bounds as setGoalWeightSchema.
export const recordGoalZoneChoiceSchema = z.discriminatedUnion("choice", [
  z.object({ choice: z.literal("keep_current_weight") }),
  z.object({ choice: z.literal("think_about_it") }),
  z.object({
    choice: z.literal("set_new_goal"),
    goalWeightKg: z.number().min(20).max(500),
  }),
]);
export type RecordGoalZoneChoice = z.infer<typeof recordGoalZoneChoiceSchema>;

// What the doctor sees on the patient page (header + monthly review): the
// latest confirmation in any status, so a closed one still explains itself,
// plus her own F-02 answer to "Har du redan nått din målvikt eller målzon?".
// That answer is information from the patient and nothing more: Emelie,
// "ska sparas och visas för läkaren som patientuppgift, men ska inte ensamt"
// "starta CP-60".
//
// TES-1162 — intake v16 drops that question and asks a transfer patient
// (F-02B) instead "Ligger din nuvarande vikt inom den målvikt eller målzon du
// arbetar mot?". Emelie 2026-10-07: "Målstatus är alltid patientrapporterat
// beslutsunderlag. Svaret får inte automatiskt sätta verified_treatment_phase
// eller styra behandlingsbeslut." So it is shown, with the day she answered,
// and read by nothing else. `intakeGoalAlreadyReached` stays for answers
// given to v10–v15.
export const WEIGHT_WITHIN_GOAL_VALUES = [
  "ja",
  "nej",
  "osaker",
  "ingen_malvikt",
] as const;
export const goalZoneDoctorSummarySchema = z.object({
  confirmation: goalZoneConfirmationSchema.nullable(),
  intakeGoalAlreadyReached: z.enum(["ja", "nej", "osaker"]).nullable(),
  intakeWeightWithinGoal: z.enum(WEIGHT_WITHIN_GOAL_VALUES).nullable(),
  /** When she submitted the declaration both answers above come from. */
  intakeSubmittedAt: z.string().nullable(),
});
export type GoalZoneDoctorSummary = z.infer<typeof goalZoneDoctorSummarySchema>;
